import React, {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useState,
} from 'react';
import cn from 'classnames';

import {
  SortableContainer,
  SortableElement,
  SortableContainerProps,
  SortableElementProps,
  SortableHandle,
  SortStartHandler,
} from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import { Icon, IconButton } from 'components';
import { TextArea } from 'components/form';

import { FieldStateOption } from 'components/Job/Job.component';
import styles from './DragAndDrop.module.scss';

interface ISortableHandleElement {
  children: React.ReactNode;
  className: string;
}

interface ISortableItem extends SortableElementProps {
  children: React.ReactNode;
  className?: string;
}

interface ISortableContainer extends SortableContainerProps {
  children: React.ReactNode;
  className: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DndTrigger: React.ComponentClass<ISortableHandleElement, any> =
  SortableHandle(
    ({
      children,
      className,
    }: {
      children: React.ReactNode;
      className: string;
    }) => <div className={className}>{children}</div>
  );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DndItem: React.ComponentClass<ISortableItem, any> = SortableElement(
  ({ children }: { children: React.ReactNode }) => <div>{children}</div>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DndList: React.ComponentClass<ISortableContainer, any> =
  SortableContainer(
    ({
      children,
      className,
    }: {
      children: React.ReactNode;
      className: string;
    }) => {
      return <div className={className}>{children}</div>;
    }
  );
export interface DragAndDropProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  placeholder: string | string[];
  fieldState: Array<FieldStateOption>;
  setFieldState: Dispatch<SetStateAction<Array<FieldStateOption>>>;
}

export const DragAndDrop: React.FC<DragAndDropProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    placeholder,
    fieldState,
    setFieldState,
  }: DragAndDropProps) => {
    const [dragItemIndex, setDragItemIndex] = useState<number | null>(null);
    const contentClassNames = cn(
      {
        [styles.DragAndDrop]: true,
      },
      className
    );

    const onSortStart: SortStartHandler = ({ index }) => {
      setDragItemIndex(index);
      document.body.style.cursor = 'grabbing';
    };

    const onSortEnd = ({
      oldIndex,
      newIndex,
    }: {
      oldIndex: number;
      newIndex: number;
    }): void => {
      setDragItemIndex(null);
      document.body.style.cursor = 'default';
      setFieldState(arrayMoveImmutable(fieldState, oldIndex, newIndex));
    };

    const onRemove = (id: string) => {
      setFieldState((list) => list.filter((res) => res.id !== id));
    };
    const onChange = (
      event: React.ChangeEvent<HTMLTextAreaElement>,
      item: FieldStateOption
    ) => {
      event.target.style.height = 'inherit';
      event.target.style.height = `${event.target.scrollHeight + 4}px`;

      setFieldState((list) =>
        list.map((res) =>
          res.id === item.id
            ? {
                id: item.id,
                value: event.target.value,
                isHovered: item.isHovered,
              }
            : res
        )
      );
    };

    const onMount = (ref: React.RefObject<HTMLTextAreaElement> | null) => {
      requestAnimationFrame(() => {
        if (ref && ref.current) {
          ref.current.style.height = 'inherit';
          ref.current.style.height = `${ref.current.scrollHeight + 2}px`;
        }
      });
    };

    const onMountRef = useCallback(
      ({
        elem,
        item,
      }: {
        elem: HTMLTextAreaElement | null;
        item: FieldStateOption;
      }) => {
        if (elem && item.toBeFocussed) {
          elem.focus();
          setFieldState((list) =>
            list.map((item) => ({ ...item, toBeFocussed: false }))
          );
        }
      },
      []
    );

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="DragAndDrop"
      >
        <DndList
          lockAxis="y"
          lockToContainerEdges={true}
          useDragHandle
          onSortStart={onSortStart}
          onSortEnd={onSortEnd}
          className={styles.DragAndDropContainer}
        >
          {fieldState.map((item, index) => {
            const placeholderString =
              placeholder[
                index >= placeholder.length ? placeholder.length - 1 : index
              ];
            const showControls =
              dragItemIndex === null ? item.isHovered : dragItemIndex === index;

            return (
              <DndItem key={item.id} index={index}>
                <div
                  className={styles.textareaContainer}
                  onMouseEnter={() => {
                    setFieldState((list) =>
                      list.map((res) =>
                        res.id === item.id
                          ? {
                              ...res,
                              isHovered: true,
                            }
                          : res
                      )
                    );
                  }}
                  onMouseLeave={() => {
                    setFieldState((list) =>
                      list.map((res) =>
                        res.id === item.id
                          ? {
                              ...res,
                              isHovered: false,
                            }
                          : res
                      )
                    );
                  }}
                >
                  <DndTrigger className={styles.drag}>
                    {showControls ? (
                      <Icon iconName="drag-and-drop" size="small" />
                    ) : (
                      <div className={styles.wrapper}></div>
                    )}
                  </DndTrigger>
                  <TextArea
                    rows={1}
                    placeholder={
                      typeof placeholder !== 'string'
                        ? placeholderString
                        : placeholder
                    }
                    value={item.value}
                    onChange={(event) => onChange(event, item)}
                    isValid={true}
                    textAreaClassName={cn(styles.textarea)}
                    className={styles.textareaDiv}
                    onMount={onMount}
                    ref={(elem) => onMountRef({ elem, item })}
                  />
                  {showControls ? (
                    <IconButton
                      iconName="close"
                      size="small"
                      onClick={() => onRemove(item.id)}
                      className={styles.remove}
                    />
                  ) : (
                    <div className={styles.wrapper}></div>
                  )}
                </div>
                <div className={styles.gapInput}></div>
              </DndItem>
            );
          })}
        </DndList>
      </div>
    );
  }
);

DragAndDrop.displayName = 'DragAndDrop';
